export const releaseMdString = `

##### Highlights - Version 3.13:

- **NEW SECTOR! Pharmaceuticals**: Covering key materials, chemicals, excipients, and process inputs, solvent recovery and recycling, pharmaceutical-specific waste management, and regionalized supply chains. All datasets are fully aligned with the first Product Category Rule (PCR), PAS 2090.

- **Textiles**: Strengthens textile raw-material coverage with new Tier 4 datasets for cotton and flax, plus a new rescutching dataset that extends the European flax supply chain.

- **Pulp & Paper**: New datasets for corrugated board, kraft paper, and cartonboard production in Europe; cartonboard production is now separated from folding carton conversion.

- **Battery Supply Chain**: 24 new battery module and pack datasets covering different sizes and chemistries, based on BatPaC. Improvements to lithium and graphite supply chains, lithium extraction, and regionalizing calcined petroleum coke (CPC) production.

- **Chemicals & Plastics**: Improved coverage of key products, including formaldehyde, acetaldehyde, and polystyrene grades. Additionally, 12 new products.

- **Electricity**: We updated electricity market mixes for all countries with 2023 data, and China, India, Brazil, Switzerland, Canada, and the U.S. have been updated with 2024 data.

- **Other sectors**: Improvements to aluminium in metals, two important updates to infrastructure datasets in building & construction, plus further updates and corrections across the database.

For further details about everything included in version 3.13, visit our [Knowledge Base](https://support.ecoinvent.org/ecoinvent-version-3.13).

&nbsp;
&nbsp;

---

##### The ecoinvent API is now available

The ecoinvent API is a programmatic interface to the ecoinvent database. It gives developers direct access to over 26,000 datasets and more than 600 impact indicators on demand—without manually downloading files, managing files locally, or switching between tools.

The API is built for teams who want life cycle data to behave like any other data layer in their stack: available when needed, always current, and easy to integrate.

&nbsp;

**What You Can Do with the ecoinvent API**

- **Embed sustainability into any product**: Build life cycle data directly into your applications, dashboards, and customer-facing tools.

- **Automate the work you used to do manually**: Run batch calculations, generate reports, and update product footprints programmatically instead of through manual exports.

- **Access the latest data automatically**: Every API call returns the most current version of the ecoinvent database. No more checking which file you have or pushing updates manually.

- **Search precisely for what you need**: Pull individual datasets or impact indicators on demand, instead of working with full database files.

- **Scale as your usage grows**: Whether you're processing data for one product or plenty, your integration scales dynamically with you.

&nbsp;

The API complements the existing ways of accessing ecoinvent data—it doesn't replace them.

- **ecoQuery**: The best place for interactive exploration, dataset review, and manual lookups.

- **XML database files**: For users who prefer working offline or with full bulk datasets.

- **ecoinvent API**: The right fit when ecoinvent data needs to live inside your own systems. Accessed programmatically, returned on demand, and always current.

&nbsp;

Are you interested in a free trial of the ecoinvent API? [Get in touch with us here](https://ecoinvent.org/contact-us/).

&nbsp;
&nbsp;

---

##### Contextual Help

You can now get instant explanations for technical terms and features without leaving your workflow.

&nbsp;

**What's new**

- **Inline explanations**: Look for the question mark icons next to technical terms and click them for a brief explanation.

- **Quick access to details**: Each popover includes a link to the full help article when you need more information.

&nbsp;

**How it works**

When you encounter unfamiliar terminology, the question mark icon provides context without interrupting your workflow. No need to search separately for help—the guidance appears exactly where you need it.

&nbsp;

Start exploring—contextual help is already live across ecoQuery.

&nbsp;
&nbsp;

---

##### Semantic Search

Find the right datasets faster with a search that understands what you're looking for.

Traditional search matches your keywords to dataset names and descriptions. Our new contextual search goes further—it understands the relationships and meaning behind your terms to surface more relevant results.

&nbsp;

**What's improved**

- **Smarter matching**: Search recognizes synonyms, related concepts, and context. Entered "steel" into ecoQuery's search bar? You'll now also see results for "steel production," "iron and steel," and "ferrous metals."

- **Better relevance**: Results are ranked by how well they match your intent, not just keyword frequency.

- **Faster discovery**: Spend less time filtering through irrelevant results and more time on your analysis.

&nbsp;

Start using semantic search today—just search as you normally would. The improved results are already live.

&nbsp;
&nbsp;

---

##### Highlights - Version 3.12:

- **Chemicals & Plastics**: Regionalization of key chemical building blocks (petrochemicals) with an expanded geographical coverage from four to twenty areas. These petrochemicals are essential for many value chains and carry major environmental impact. Updated data on key chemicals (styrene, acetylene, etc.) relevant to battery production. New data on plastic processing and modeling of several new products. Introduction of new flows of grouped organic compounds to be used when specific chemicals are not available.

- **Fuels**: Regionalization of Natural Gas Liquids (NGLs) production and fractionation as well as regionalized Helium production. This helps reflect regional specificities in production by increasing the sector coverage with new geographies (UAE, USA, Brazil, Saudi Arabia, India, Russia, Canada etc.).

- **Metals**: Updates on the aluminum production chain and corresponding electricity datasets. New data on zamak production, mostly based on secondary zinc, and new data for steel alloys, serving downstream sectors such as construction, automotive, and consumer goods.

- **Electricity**: Updated electricity mixes using statistics from 2022 and 2023. Electricity mixes are vital for modelling purposes and represent one of the most used parts of the ecoinvent database.

- **Forestry & Wood**: New primary data from Finnish forest industries, focusing on pine, spruce, and birch. This allows for filling data gaps and more geographic granularity. Especially useful for paper, construction, and fuel industries.

- **Textiles**: New data for hemp fiber production, remodeling of flax fiber. Harmonized nomenclature and finetuned values, enabling higher accuracy and user friendliness.

- **Other sectors**: Inconsistency corrections in the agricultural sector, updated data on seagoing vessels and road transport, updated waste fate in paper sack production, technical updates on battery production, and updates in the waste sector improving consistency and integrating recycling flows.

For further details about everything included in version 3.12, visit our [Knowledge Base](https://support.ecoinvent.org/ecoinvent-version-3.12).

&nbsp;
&nbsp;

##### Highlights - Version 3.11:

- **Fuels**: Revised natural gas data for 57 countries, updated estimates for pipeline and LNG transport, and improved data for coal and lignite production, with enhanced regional details.
 
- **Energy:** Updated electricity market mixes for all countries with 2021 data and 2022 data for Brazil, China, and Switzerland.
 
- **Chemicals and Plastics:** Improved data coverage for key chemical precursors (e.g., carbon monoxide), polyvinyl chloride, unsaturated polyester/vinyl ester resins, and new composites manufacturing datasets.
 
- **Batteries:** New datasets for lithium-ion chemistries, including NMC532 and NMC622, new and updated datasets for natural graphite and synthetic graphite, and expanded coverage of salts and electrolytes for emerging technologies of sodium-ion batteries.
 
- **Building and Construction:** New cement and clinker production data reflecting specific Canadian regional practices and updates covering a variety of building materials.
 
- **Waste:** Regionalized municipal waste treatment data for 27 EU countries, Switzerland, Norway, Iceland, and the United Kingdom, plus new datasets on mechanical and chemical recycling processes of waste plastic in Europe and China.
 
- **Agriculture:** 136 new datasets for crops across different regions in Switzerland covering conventional and organic farming methods.
 
- **Transport:** Updates to the circulating fleet of passenger vehicles and new datasets on aircraft maintenance.
 
- **LCIA methods:** Revised and updated modeling of biogenic carbon flows to allow for extended climate change assessment including biogenic carbon dioxide.
 
For further details about everything included in version 3.11, visit our [Knowledge Base](https://support.ecoinvent.org/ecoinvent-version-3.11).

&nbsp;
&nbsp; 

##### Version 3.10.1

We identified two significant issues in version 3.10 of the ecoinvent database, which affects certain impact assessment indicators across all system models. To address these issues, we have released **version 3.10.1** concurrently with version 3.11 to provide fixes for users of version 3.10.

- **For users of version 3.10:** We recommend upgrading to version 3.10.1, especially if their work involves the affected impact categories or unit process data related to coke production or Brazilian electricity markets. 
 
- **For users of earlier versions:** These issues do not affect your work. When you upgrade to the latest version, we advise starting directly with version 3.10.1 or 3.11. 
 
For detailed information about version 3.10.1, please refer to our [Knowledge Base](https://support.ecoinvent.org/ecoinvent-version-3.10.1).
`;
